import { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileIcon, Download, Plus, Upload, FileText, File } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Document {
  id: string;
  patient_id: string;
  name: string;
  type: string;
  url: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

interface DocumentsCardProps {
  documents: Document[];
  patientId?: string;
}

export function DocumentsCard({ documents, patientId }: DocumentsCardProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { profile } = useAuth();
  const queryClient = useQueryClient();

  // Sort documents by date (newest first)
  const sortedDocuments = [...(documents || [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !patientId || !profile?.id) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const file = files[0];
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
      const filePath = `documents/${patientId}/${fileName}`;
      
      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('patient-documents')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });
      
      // Set progress to 100% after successful upload
      setUploadProgress(100);
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { data: publicUrlData } = supabase.storage
        .from('patient-documents')
        .getPublicUrl(filePath);
      
      if (!publicUrlData.publicUrl) throw new Error("Failed to get public URL");
      
      // Create document record
      const { data: documentData, error: documentError } = await supabase
        .from('patient_documents')
        .insert({
          patient_id: patientId,
          clinic_id: profile.clinic_id,
          document_type: 'other', // Default type, could be enhanced to detect based on file type
          file_name: file.name,
          file_path: filePath,
          uploaded_by: profile.id,
          notes: ''
        })
        .select();
      
      if (documentError) throw documentError;
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ['patient-detail', patientId] });
      
    } catch (error) {
      console.error("Error uploading document:", error);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset file input
      e.target.value = '';
    }
  };

  const handleDownload = (url: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (e) {
      return dateString;
    }
  };

  const getDocumentIcon = (type: string) => {
    if (type.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (type.includes('image')) return <FileIcon className="w-5 h-5 text-blue-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle>Documents</CardTitle>
          <CardDescription>Patient medical documents</CardDescription>
        </div>
        {patientId && profile?.id && (
          <div className="relative">
            <input
              type="file"
              id="file-upload"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={handleFileUpload}
              disabled={isUploading}
            />
            <Button 
              size="sm" 
              className="ml-auto"
              disabled={isUploading}
            >
              <Upload className="w-4 h-4 mr-1" />
              {isUploading ? `Uploading ${uploadProgress}%` : "Upload Document"}
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {sortedDocuments.length > 0 ? (
          <div className="space-y-2">
            {sortedDocuments.map((doc) => (
              <div key={doc.id} className="flex items-center justify-between p-2 border rounded-md hover:bg-gray-50">
                <div className="flex items-center space-x-3">
                  {getDocumentIcon(doc.type)}
                  <div>
                    <p className="font-medium text-sm truncate max-w-[200px]">{doc.name}</p>
                    <p className="text-xs text-gray-500">{formatDate(doc.created_at)}</p>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => handleDownload(doc.url, doc.name)}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600">No documents available</p>
            {patientId && profile?.id && (
              <div className="relative mt-4">
                <input
                  type="file"
                  id="file-upload-empty"
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  onChange={handleFileUpload}
                  disabled={isUploading}
                />
                <Button variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  Upload First Document
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
